/* eslint-disable @typescript-eslint/no-explicit-any */
import { cookie, findTopDomain } from '@turnercode/cdp-cookie';
import { Psm } from '../Psm';
import { PayloadCore } from '../payloadCore';
import { cookiesEnabled, getReferrer, localStorageAccessible } from './browser';
import pkg from '../../package.json';

const win = window as any;
const doc = document as any;

export type HydrateOptions = {
  psm: Psm;
  core: PayloadCore;
};

/**
 * Adapted from https://github.com/turnercode/digital-ads/blob/develop/src/Modules/Krux/index.js#L33
 */
export function getKruxId(): string {
  if (localStorageAccessible()) {
    return win.localStorage.getItem('kxkuid');
  } else if (cookiesEnabled()) {
    return cookie.get('kxkuid') as string;
  } else {
    return null;
  }
}

export function getAttUuid(): string {
  if (cookiesEnabled()) {
    const att = cookie.get('firstpartyuid') as { id: string; timestamp: string };
    if (att && typeof att === 'object' && 'id' in att) {
      return att.id;
    } else {
      return null;
    }
  }
  return null;
}

function grabIDFromAMCVCookie(id) {
  const AMCVCookie = cookie.get('AMCV_7FF852E2556756057F000101@AdobeOrg') as string;

  if (AMCVCookie) {
    // The AMCV cookie stores ids in the following format: key1 | value1 | key2 | value2
    const AMCVSplit = AMCVCookie.split('|');

    // grabs the index of the id key
    const idKeyIndex = AMCVSplit.indexOf(id);

    // condition to ensure the id exists on the AMCV cookie, if not skip and return null below
    if (idKeyIndex != -1) {
      // grabs the id value using the id key index as a base and increments by 1 given
      const idValueIndex = idKeyIndex + 1;

      return AMCVSplit[idValueIndex];
    }
  }
  return null;
}

export function getEcid(): string | null {
  const s_ecid = cookie.get('s_ecid') as string;
  const s_vi = cookie.get('s_vi') as string;
  const ecid = s_ecid ? s_ecid : s_vi ? s_vi : grabIDFromAMCVCookie('MCMID');
  return ecid;
}

export function getConvivaId(): string | null {
  if (localStorageAccessible()) {
    const convivaIdKey = ['Conviva/sdkConfig', 'Conviva.sdkConfig', 'top-player::Conviva.sdkConfig'];
    let clientId = null;
    for (let i = 0; i < convivaIdKey.length; i++) {
      clientId = win.localStorage.getItem(convivaIdKey[i]);
      if (clientId) {
        const convivaObj = JSON.parse(clientId);
        return convivaObj.clId;
      }
    }
  }
  return null;
}

export function getZionId(): string | null {
  let zionId = null;
  if (win.ZionMessageBus) {
    const zionBus = win.ZionMessageBus.getInstance();
    const idObjects = zionBus.watching.id_found.messageQueue;

    if (idObjects && idObjects instanceof Array) {
      const zaidObject = idObjects.find((idObject) => idObject.message?.type === 'ZAID');
      zionId = zaidObject ? zaidObject.message.value : null;
    }
  }
  return zionId;
}

export const hydratePayload = (psm: Psm, core: PayloadCore) => {
  core.setPlatform(psm.config.platform);
  core.addEntry('companyName', 'WarnerMedia');
  core.setBrand(psm.config.brand);
  core.setSubBrand(psm.config.subBrand);
  core.setProductName(psm.config.brand);
  core.setLibrary({ name: 'PrismJS', version: pkg.version, initConfig: psm.config });
  core.setDevice({
    type: win.navigator.platform,
    userAgent: win.navigator.userAgent,
  });
  core.setNavigationProperties({
    url: win.location.href,
    rootDomain: findTopDomain(win.location.href),
    referrer: getReferrer(),
    path: win.location.pathname,
    search: win.location.search,
    title: doc.title,
  });
  core.setClientResolvedIp(psm.location.ip_address);
  core.setLocation({
    city: psm.location.states[0].cities[0],
    state: psm.location.states[0].state,
    country: psm.location.country,
    zip: psm.location.states[0].zipcodes[0],
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || null,
    locale: win.navigator.language,
    language: win.navigator.language.split('-')[0] || null,
  });
  core.setConsentProperties({ uspString: cookie.get('usprivacy') as string });
  core.setWMUKID(psm.wmukid as string);
  core.setEventProperties({
    cookiesEnabled: cookiesEnabled(),
    doNotTrack: win.navigator.doNotTrack || win.doNotTrack ? true : false,
    featureFlagValues: psm.flags,
    cookies: cookie.getAll(),
    automatedTest: psm.config.psmEnvironment === 'AUTOMATED_TEST',
  });
  core.setSessionProperties(psm.session);
  core.setThirdPartyIds({
    attuuid: getAttUuid(),
    cdpid: psm.getCDPID(),
    kruxid: getKruxId(),
    ecid: getEcid(),
    convivaid: getConvivaId(),
    zionid: getZionId(),
    wmhhid: cookie.get('wmhhid') as string,
    wminid: cookie.get('wminid') as string,
  });
};
